import type { ComputedRef } from "vue";
import {
  Well_Test_Liquid_Input_Options_Enum,
  type Override_Options_Enum
} from "gql/api";
import { computed } from "vue";
import {
  getWellFlowingPressure,
  getWellPressureAtDepth
} from "@/libs/calculations/ipr";
import { getPVTFluidProperties } from "@/libs/calculations/oilPhasePVT";
import { getMultiphaseTotalDynamicHeadFt } from "@/libs/calculations/pumps";
import type { FluidPropertiesPipelineResults } from "../fluidPropertiesPipeline";
import { performFluidPropertiesCalculations } from "../fluidPropertiesPipeline";
import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import type { HPSFluidPropertiesPipelineResults } from "../hpsFluidPropertiesPipeline";
import { performHPSFluidPropertiesCalculations } from "../hpsFluidPropertiesPipeline";
import type { FluidPropertiesCtx } from "./context";

// Pressures and the fluid properties pipeline results at each depth.
export const defineFluidProperties = (ctx: FluidPropertiesCtx) => {
  const {
    overrides,
    _equipment,
    _wellProperties,
    _parameters,
    _correlations,
    isScenarioEspShore,
    totalLiquidFlowRateStbd,
    orderedIntakesInfo,
    primaryPump,
    primarySeal,
    primaryIntake,
    sensorLengthFt,
    boltOnDischargeDepthFt,
    completePumpDepthFt,
    bottomOfPumpsDepthFt,
    bottomOfIntakesDepthFt,
    bottomOfSealsDepthFt,
    bottomOfMotorDepthFt,
    bottomOfEquipmentDepthFt,
    tubingsDepthTVDFt,
    bottomOfDischargeDepthTVDFt,
    bottomOfPumpsTVDFt,
    bottomOfIntakesTVDFt,
    bottomOfSealsTVDFt,
    bottomOfMotorTVDFt,
    bottomOfEquipmentTVDFt,
    casingInteriorDiameterAtIntakeIn,
    casingInteriorDiameterAtSensorIn,
    bottomCasingInteriorDiameterIn,
    tubingInternalDiameterAtPumpDischargeIn,
    sensorCrossSectionFt2,
    motorCrossSectionFt2,
    sealsCrossSectionFt2,
    intakeCrossSectionFt2,
    topTubingCrossSectionFt2,
    datumPointTVD,
    customFluidProperties,
    customViscosityProperties,
    entryRows,
    bottomholeTemperatureF,
    surfaceTemperatureF,
    desiredLiquidRateSTDB,
    waterCutRatio,
    basicData,
    casingInteriorDiameterAtSealsIn,
    casingInteriorDiameterAtMotorIn,
    wellTestFlowRates,
    desiredFlowRates
  } = ctx;

  // Get fluid properties from the bottom of the well
  // This needs to be separate from the fluid properties pipeline
  // since we use the fluidDensityAtReservoir to compute wellFlowingPressures
  // at different downhole intervals. Those are then used to compute other
  // fluid property sets. This is outside of the equipment.
  const fluidViscosityCPOverride = computed(() => {
    return _wellProperties.value?.fluid_viscosity_override_cp ?? null;
  });

  // D22 — condensate SG and the Ovalle gas condensate ratio
  const condensateApi = computed(() => {
    return _wellProperties.value?.condensate_api_override ?? null;
  });

  const fluidPropertiesAtReservoirContext = computed(() => {
    const pipeDiameterIn = isScenarioEspShore.value
      ? bottomCasingInteriorDiameterIn.value
      : primaryIntake.value?.r_intake?.inner_diameter_inches;
    return getFluidPropertiesContext(
      basicData.value,
      _correlations.value,
      basicData.value.staticBottomholePressurePSIA,
      bottomholeTemperatureF.value,
      pipeDiameterIn,
      wellTestFlowRates.value,
      undefined,
      undefined,
      customFluidProperties.value,
      customViscosityProperties.value,
      _wellProperties.value?.bubble_point_pressure_psia,
      _wellProperties.value?.dew_point_pressure_psia_override,
      0,
      0,
      {
        fluidViscosityCPOverride: fluidViscosityCPOverride.value
      },
      condensateApi.value
    );
  });

  const fluidPropertiesAtReservoir = computed(
    () => fluidPropertiesAtReservoirContext.value.fluidProperties
  );

  // Get fluid properties from the top of the well.
  // This is seaparate from the fluid properties pipeline since
  // this is outside of the equipment, using well test flow rates.
  const fluidPropertiesContextAtSurface = computed(() => {
    return getFluidPropertiesContext(
      basicData.value,
      _correlations.value,
      _parameters.value?.bottom_casing_pressure_psia,
      surfaceTemperatureF.value,
      basicData.value.topCasingInteriorDiameterIn,
      wellTestFlowRates.value,
      undefined,
      undefined,
      customFluidProperties.value,
      customViscosityProperties.value,
      _wellProperties.value?.bubble_point_pressure_psia,
      _wellProperties.value?.dew_point_pressure_psia_override,
      topTubingCrossSectionFt2.value,
      0,
      {
        fluidViscosityCPOverride: fluidViscosityCPOverride.value
      },
      condensateApi.value
    );
  });

  // Use user-given bubble point or bubble point calculated from
  // fluidPropertesAtReservoir for calculations
  const selectedBubblePointPressurePSIA = computed(() => {
    const bubblePointPressurePSIA =
      _wellProperties.value?.bubble_point_pressure_psia ||
      fluidPropertiesAtReservoir.value.bubblePointPressurePSIA;

    return Math.max(bubblePointPressurePSIA, 0);
  });

  const selectedDewPointPressurePSIA = computed(() => {
    const dewPointPressurePSIA =
      _wellProperties.value?.dew_point_pressure_psia_override ||
      fluidPropertiesAtReservoir.value.dewPointPressurePSIA;

    return Math.max(dewPointPressurePSIA, 0);
  });

  const selectedFluidViscosityCP = computed(() => {
    return (
      fluidViscosityCPOverride.value ??
      fluidPropertiesAtPumpIntake.value.fluidViscosityCP
    );
  });

  const selectedNPSHaFt = computed(() => {
    if (isScenarioEspShore.value) {
      return 0;
    }
    // This calc will need redone once we have a better calc for npsha
    // instead of just calling getMultiphaseTotalDynamicHeadFt
    const npsha =
      _wellProperties.value?.npsha_ft ??
      getMultiphaseTotalDynamicHeadFt(
        _wellProperties.value?.hps_pump_inlet_pressure_psia ?? 0,
        fluidPropertiesAtPumpIntake.value.fluidDensityLBMFT3
      );
    return Math.max(npsha, 0);
  });

  const oilPhaseGasSolubilityAtBubblePointSCFSTB = computed(() => {
    const { solutionGasRso } = getPVTFluidProperties(
      basicData.value,
      wellTestFlowRates.value.gasOilRatioSCFSTB,
      _correlations.value,
      selectedBubblePointPressurePSIA.value,
      bottomholeTemperatureF.value,
      fluidPropertiesAtReservoir.value.condensateApi,
      fluidPropertiesAtReservoir.value.condensateSpecificGravity,
      selectedBubblePointPressurePSIA.value,
      customFluidProperties.value
    );

    return solutionGasRso;
  });

  // P_wf
  // TODO: Once flow rates are decoupled, we could update getWellFlowingPressure and getWellFlowingPressureUsingVogel
  // to accept water/oil rates from flow rates at datum instead of only the total liquid flow rate.
  const wellFlowingPressureAtDatumPSIA: ComputedRef<number> = computed(() => {
    let pi = null;
    if (
      _wellProperties.value?.well_test_liquid_input_type ===
      Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
    ) {
      pi = _wellProperties.value?.well_test_productivity_index;
    }
    return getWellFlowingPressure(
      _parameters.value?.flow_model as string,
      desiredLiquidRateSTDB.value,
      totalLiquidFlowRateStbd.value,
      basicData.value.staticBottomholePressurePSIA,
      _wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
      fluidPropertiesAtReservoir.value.bubblePointPressurePSIA,
      waterCutRatio.value,
      pi
    );
  });

  const wellFlowingPressureSurfacePSIA = computed(() =>
    getWellPressureAtDepth(
      bottomOfPumpsTVDFt.value,
      fluidPropertiesAfterAllGasSeparationAtPump.value.fluidDensityLBMFT3,
      ctx.pumpIntakePressurePSIA.value,
      entryRows.value,
      0,
      1
    )
  );

  const hpsPipeLineCalculationsParameters = computed(() => {
    return {
      // well properties
      basicData: basicData.value,
      correlations: _correlations.value,
      desiredFlowRates: desiredFlowRates.value,
      requiredDischargePressurePSIA: ctx.requiredDischargePressurePSIA.value,
      bubblePointPressurePSIA: selectedBubblePointPressurePSIA.value,
      customFluidProperties: customFluidProperties.value,
      customViscosityProperties: customViscosityProperties.value,
      condensateApi: condensateApi.value,
      dewPointPressurePSIA: selectedDewPointPressurePSIA.value,
      fluidPropertiesOverrides: {
        fluidViscosityCPOverride: fluidViscosityCPOverride.value
      },
      primaryIntakeInnerDiameterInches:
        primaryIntake.value?.r_intake?.inner_diameter_inches,
      primaryPumpInnerDiameterInches:
        primaryPump.value?.r_pump?.housing_inner_diameter_inches
    };
  });

  const hpsStageFluidProperties = computed<HPSFluidPropertiesPipelineResults>(
    () => {
      const hpsStageFluidPropertiesValues =
        performHPSFluidPropertiesCalculations(
          hpsPipeLineCalculationsParameters.value.basicData,
          hpsPipeLineCalculationsParameters.value.correlations,
          hpsPipeLineCalculationsParameters.value.desiredFlowRates,
          hpsPipeLineCalculationsParameters.value.requiredDischargePressurePSIA,
          hpsPipeLineCalculationsParameters.value.bubblePointPressurePSIA,
          hpsPipeLineCalculationsParameters.value.condensateApi,
          hpsPipeLineCalculationsParameters.value.dewPointPressurePSIA,
          hpsPipeLineCalculationsParameters.value.customFluidProperties,
          hpsPipeLineCalculationsParameters.value.customViscosityProperties,
          hpsPipeLineCalculationsParameters.value.fluidPropertiesOverrides,
          hpsPipeLineCalculationsParameters.value
            .primaryIntakeInnerDiameterInches,
          hpsPipeLineCalculationsParameters.value.primaryPumpInnerDiameterInches
        );
      return hpsStageFluidPropertiesValues;
    }
  );

  const pipeLineCalculationsParameters = computed(() => {
    return {
      // well properties
      basicData: basicData.value,
      _correlations: _correlations.value,
      wellTestFlowRates: wellTestFlowRates.value,
      desiredFlowRates: desiredFlowRates.value,
      wellFlowingPressureAtDatumPSIA: wellFlowingPressureAtDatumPSIA.value,
      datumPointTVD: datumPointTVD.value,
      reservoirFluidDensity:
        fluidPropertiesAtReservoir.value.fluidDensityLBMFT3,
      bubblePointPressurePSIA: selectedBubblePointPressurePSIA.value,
      includeCasingFrictionLoss:
        _parameters.value?.include_casing_friction_loss,
      condensateApi: condensateApi.value,
      dewPointPressurePSIA: selectedDewPointPressurePSIA.value,
      fluidPropertiesOverrides: {
        fluidViscosityCPOverride: fluidViscosityCPOverride.value
      },
      entryRows,
      customFluidProperties: customFluidProperties.value,
      customViscosityProperties: customViscosityProperties.value,
      tdhTuningFactor: _correlations.value?.tdh_tuning_factor_ratio || 1,
      // equipment properties
      bottomOfEquipmentMDFt: bottomOfEquipmentDepthFt.value,
      bottomOfEquipmentTVDFt: bottomOfEquipmentTVDFt.value,
      bottomOfSensorMDFt: bottomOfEquipmentDepthFt.value,
      bottomOfSensorTVDFt: bottomOfEquipmentTVDFt.value,
      sensorLengthFt: sensorLengthFt.value,
      sensorOuterDiameterInches:
        _equipment.value?.r_sensor?.outer_diameter_inches || 0,
      sensorCrossSectionFt2: sensorCrossSectionFt2.value,
      bottomOfTubingMDFt: boltOnDischargeDepthFt.value,
      tubingsDepthTVDFt: tubingsDepthTVDFt.value,
      topTubingOuterDiameterInches:
        _wellProperties.value?.r_top_tubing?.outer_diameter_inches || 0,
      bottomTubingOuterDiameterInches:
        _wellProperties.value?.r_bottom_tubing?.outer_diameter_inches || 0,
      topOfPumpsMDFt: completePumpDepthFt.value,
      topOfPumpsTVDFt: bottomOfDischargeDepthTVDFt.value,
      boltOnDischargeOuterDiameterInches:
        _equipment.value?.r_bolt_on_discharge?.outer_diameter_inches || 0,
      // pumps
      bottomOfPumpsTVDFt: bottomOfPumpsTVDFt.value,
      bottomOfPumpsMDFt: bottomOfPumpsDepthFt.value,
      primaryPumpOuterDiameterInches:
        primaryPump.value?.r_pump?.housing_outer_diameter_inches || 0,
      // intakes
      orderedIntakesInfo: orderedIntakesInfo.value,
      primaryIntakeOuterDiameterInches:
        primaryIntake.value?.r_intake?.outer_diameter_inches,
      primaryIntakePortSquareInches:
        primaryIntake.value?.r_intake?.port_area_square_inches,
      bottomOfIntakesTVDFt: bottomOfIntakesTVDFt.value,
      bottomOfIntakesMDFt: bottomOfIntakesDepthFt.value,
      intakePackerInstalled: _parameters.value?.intake_packer_installed,
      naturalGasSeparationOverrideType:
        _parameters.value?.natural_gas_separation_override_type,
      intakeGasSeparationOverrideType:
        _parameters.value?.intake_gas_separation_override_type,
      naturalOverrideGasSeparationPercent:
        _parameters.value?.natural_override_gas_separation_percent,
      intakeOverrideGasSeparationPercent:
        _parameters.value?.intake_override_gas_separation_percent,
      intakeCrossSectionFt2: intakeCrossSectionFt2.value,
      // seals
      bottomOfSealsMDFt: bottomOfSealsDepthFt.value,
      bottomOfSealsTVDFt: bottomOfSealsTVDFt.value,
      sealsCrossSectionFt2: sealsCrossSectionFt2.value,
      primarySealOuterDiameterInches:
        primarySeal.value?.r_seal?.outer_diameter_inches,
      // motor
      motorOuterDiameterInches:
        _equipment.value?.r_motor?.outer_diameter_inches,
      motorLengthInches: _equipment.value?.r_motor?.length_inches,
      bottomOfMotorMDFt: bottomOfMotorDepthFt.value,
      bottomOfMotorTVDFt: bottomOfMotorTVDFt.value,
      motorCrossSectionFt2: motorCrossSectionFt2.value,
      effectiveCasingInteriorDiameterAtMotorIn:
        casingInteriorDiameterAtMotorIn.value,
      effectiveCasingInteriorDiameterAtSealsIn:
        casingInteriorDiameterAtSealsIn.value,
      effectiveCasingInteriorDiameterAtIntakeIn:
        casingInteriorDiameterAtIntakeIn.value,
      effectiveCasingInteriorDiameterAtSensorIn:
        casingInteriorDiameterAtSensorIn.value,
      topCasingInteriorDiameterIn: basicData.value.topCasingInteriorDiameterIn,
      topCasingLengthFt: basicData.value.topCasingLengthFt,
      bottomCasingInteriorDiameterIn: bottomCasingInteriorDiameterIn.value,
      bottomCasingLengthFt: basicData.value.bottomCasingLengthFt,
      // tubing
      tubingInternalDiameterAtPumpDischargeIn:
        tubingInternalDiameterAtPumpDischargeIn.value,
      // shroud
      shroudInteriorDiameterIn:
        _equipment.value?.r_shroud?.interior_diameter_inches || 0,
      shroudOuterDiameterIn:
        _equipment.value?.r_shroud?.outer_diameter_inches || 0,
      overrides
    };
  });

  const stageFluidProperties = computed<FluidPropertiesPipelineResults>(() => {
    const stageFluidPropertiesValues = performFluidPropertiesCalculations(
      // well properties
      pipeLineCalculationsParameters.value.basicData,
      pipeLineCalculationsParameters.value._correlations,
      pipeLineCalculationsParameters.value.wellTestFlowRates,
      pipeLineCalculationsParameters.value.desiredFlowRates,
      pipeLineCalculationsParameters.value.wellFlowingPressureAtDatumPSIA,
      pipeLineCalculationsParameters.value.datumPointTVD,
      pipeLineCalculationsParameters.value.bubblePointPressurePSIA,
      pipeLineCalculationsParameters.value.condensateApi,
      pipeLineCalculationsParameters.value.dewPointPressurePSIA,
      pipeLineCalculationsParameters.value.includeCasingFrictionLoss ?? true,
      pipeLineCalculationsParameters.value.entryRows.value,
      pipeLineCalculationsParameters.value.customFluidProperties,
      pipeLineCalculationsParameters.value.customViscosityProperties,
      pipeLineCalculationsParameters.value.fluidPropertiesOverrides,
      pipeLineCalculationsParameters.value.tdhTuningFactor,
      // equipment properties
      pipeLineCalculationsParameters.value.bottomOfEquipmentMDFt,
      pipeLineCalculationsParameters.value.bottomOfEquipmentTVDFt,
      pipeLineCalculationsParameters.value.bottomOfSensorMDFt,
      pipeLineCalculationsParameters.value.bottomOfSensorTVDFt,
      pipeLineCalculationsParameters.value.sensorLengthFt,
      pipeLineCalculationsParameters.value.sensorOuterDiameterInches,
      pipeLineCalculationsParameters.value.sensorCrossSectionFt2,
      pipeLineCalculationsParameters.value.bottomOfTubingMDFt,
      pipeLineCalculationsParameters.value.tubingsDepthTVDFt,
      pipeLineCalculationsParameters.value.topTubingOuterDiameterInches,
      pipeLineCalculationsParameters.value.bottomTubingOuterDiameterInches,
      pipeLineCalculationsParameters.value.topOfPumpsMDFt,
      pipeLineCalculationsParameters.value.topOfPumpsTVDFt,
      pipeLineCalculationsParameters.value.boltOnDischargeOuterDiameterInches,
      // pumps
      pipeLineCalculationsParameters.value.bottomOfPumpsTVDFt,
      pipeLineCalculationsParameters.value.bottomOfPumpsMDFt,
      pipeLineCalculationsParameters.value.primaryPumpOuterDiameterInches,
      // intake
      pipeLineCalculationsParameters.value.orderedIntakesInfo,
      pipeLineCalculationsParameters.value.primaryIntakeOuterDiameterInches,
      pipeLineCalculationsParameters.value.primaryIntakePortSquareInches,
      pipeLineCalculationsParameters.value.bottomOfIntakesTVDFt,
      pipeLineCalculationsParameters.value.bottomOfIntakesMDFt,
      pipeLineCalculationsParameters.value.intakePackerInstalled || false,
      pipeLineCalculationsParameters.value
        .naturalGasSeparationOverrideType as Override_Options_Enum,
      pipeLineCalculationsParameters.value
        .intakeGasSeparationOverrideType as Override_Options_Enum,
      pipeLineCalculationsParameters.value.naturalOverrideGasSeparationPercent,
      pipeLineCalculationsParameters.value.intakeOverrideGasSeparationPercent,
      pipeLineCalculationsParameters.value.intakeCrossSectionFt2,
      // seals
      pipeLineCalculationsParameters.value.bottomOfSealsMDFt,
      pipeLineCalculationsParameters.value.bottomOfSealsTVDFt,
      pipeLineCalculationsParameters.value.sealsCrossSectionFt2,
      pipeLineCalculationsParameters.value.primarySealOuterDiameterInches,
      // motor
      pipeLineCalculationsParameters.value.bottomOfMotorMDFt,
      pipeLineCalculationsParameters.value.bottomOfMotorTVDFt,
      pipeLineCalculationsParameters.value.motorCrossSectionFt2,
      pipeLineCalculationsParameters.value
        .effectiveCasingInteriorDiameterAtMotorIn,
      pipeLineCalculationsParameters.value
        .effectiveCasingInteriorDiameterAtSealsIn,
      pipeLineCalculationsParameters.value
        .effectiveCasingInteriorDiameterAtIntakeIn,
      pipeLineCalculationsParameters.value
        .effectiveCasingInteriorDiameterAtSensorIn,
      // casing geometry
      pipeLineCalculationsParameters.value.topCasingInteriorDiameterIn,
      pipeLineCalculationsParameters.value.topCasingLengthFt,
      pipeLineCalculationsParameters.value.bottomCasingInteriorDiameterIn,
      pipeLineCalculationsParameters.value.bottomCasingLengthFt,
      // tubing
      pipeLineCalculationsParameters.value
        .tubingInternalDiameterAtPumpDischargeIn,
      // shroud
      pipeLineCalculationsParameters.value.shroudInteriorDiameterIn,
      pipeLineCalculationsParameters.value.shroudOuterDiameterIn,
      pipeLineCalculationsParameters.value.overrides
    );
    return stageFluidPropertiesValues;
  });

  const fluidPropertiesAtDatum = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtDatumContext.fluidProperties
  );

  const fluidPropertiesAtMotor = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtMotorContext.fluidProperties
  );

  const fluidPropertiesAtSeals = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtSealsContext.fluidProperties
  );

  const fluidPropertiesAtSensor = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtSensorContext?.fluidProperties
  );

  const fluidPropertiesAtIntake = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtIntakeContext.fluidProperties;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAtIntakeContext.fluidProperties;
  });

  const fluidPropertiesAtWellPerfs = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtWellPerfsContext.fluidProperties
  );

  const fluidPropertiesAtPumpIntake = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAtPumpIntakeContext.fluidProperties;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAtPumpIntakeContext.fluidProperties;
  });

  const fluidPropertiesAfterAllGasSeparationAtPump = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAfterAllGasSeparationAtPumpContext.fluidProperties;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAfterAllGasSeparationAtPumpContext.fluidProperties;
  });

  const fluidPropertiesAfterAllGasSeparationAtPumpContext = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAfterAllGasSeparationAtPumpContext;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAfterAllGasSeparationAtPumpContext;
  });

  const fluidPropertiesInsideTubingContext = computed(
    () =>
      stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesInsideTubingContext
  );

  const fluidPropertiesAfterPumpDischargeTubingContext = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAfterPumpDischargeTubingContext;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAfterPumpDischargeContext;
  });

  const fluidPropertiesAfterPumpDischargeTubing = computed(() => {
    if (isScenarioEspShore.value) {
      return stageFluidProperties.value.fluidPropertiesCalculationContexts
        .fluidPropertiesAfterPumpDischargeTubingContext.fluidProperties;
    }
    return hpsStageFluidProperties.value.fluidPropertiesCalculationContexts
      .hpsFluidPropertiesAfterPumpDischargeContext.fluidProperties;
  });

  return {
    fluidViscosityCPOverride,
    condensateApi,
    fluidPropertiesAtReservoirContext,
    fluidPropertiesAtReservoir,
    fluidPropertiesContextAtSurface,
    selectedBubblePointPressurePSIA,
    selectedDewPointPressurePSIA,
    selectedFluidViscosityCP,
    selectedNPSHaFt,
    oilPhaseGasSolubilityAtBubblePointSCFSTB,
    wellFlowingPressureAtDatumPSIA,
    wellFlowingPressureSurfacePSIA,
    hpsPipeLineCalculationsParameters,
    hpsStageFluidProperties,
    pipeLineCalculationsParameters,
    stageFluidProperties,
    fluidPropertiesAtDatum,
    fluidPropertiesAtMotor,
    fluidPropertiesAtSeals,
    fluidPropertiesAtSensor,
    fluidPropertiesAtIntake,
    fluidPropertiesAtWellPerfs,
    fluidPropertiesAtPumpIntake,
    fluidPropertiesAfterAllGasSeparationAtPump,
    fluidPropertiesAfterAllGasSeparationAtPumpContext,
    fluidPropertiesInsideTubingContext,
    fluidPropertiesAfterPumpDischargeTubingContext,
    fluidPropertiesAfterPumpDischargeTubing
  };
};

export type FluidPropertiesCalcs = ReturnType<typeof defineFluidProperties>;
